import React, { useEffect, useState } from "react";
import { useDataProvider } from "react-admin";
import { getRouteTransactionMonthAndYear } from "../tools";
import {
  CircularProgress,
  Grid,
  makeStyles,
  Typography,
} from "@material-ui/core";
import RoutePricePreview from "./RoutePricePreview";
import Alert from "@material-ui/lab/Alert";
import SanitizedGrid from "../../../components/SanitizedGrid";

export interface IRoutePricePreview {
  vehicleSize: string;
  baseAmount: number;
  adjustmentAmount: number;
  busAssistantIncentive: number;
  schoolIncentive: number;
  additionalIncentive: number;
  type: "last" | "new";
}

interface IRoutePriceComponent {
  routeId: number;
  subcontractorId: number;
  schoolId: number;
  price: number;
  viewType?: "card" | "table";
}

const useStyles = makeStyles({
  loadingIndicator: {
    padding: "10px",
    marginBottom: 8,
    display: "flex",
    justifyContent: "center",
  },
  routePricingContainer: {
    marginBottom: 8,
    marginTop: 10,
  },
});

const RoutePriceComponent = ({
  routeId,
  subcontractorId,
  schoolId,
  price,
  viewType = "card",
}: IRoutePriceComponent) => {
  const provider = useDataProvider();
  const [routePrices, setRoutePrices] = useState<IRoutePricePreview[]>([]);
  const [routeName, setRouteName] = useState<string>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { month, year } = getRouteTransactionMonthAndYear();
  const classes = useStyles();

  // Get route name from routeId
  useEffect(() => {
    if (routeId) {
      setIsLoading(true);

      provider.getMany("routes", { ids: [routeId] }).then(({ data }) => {
        setRouteName(data[0]?.name); // assuming data only have 1 data because we only send 1 routeId and data.name is routeName
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  // Get last month route transactions
  useEffect(() => {
    if (routeName && subcontractorId && schoolId) {
      setIsLoading(true);

      // Reset the route prices when change route
      setRoutePrices([]);

      provider
        .getList("subcontractorRouteTransactions", {
          filter: {
            RouteName: routeName,
            SubcontractorId: subcontractorId,
            SchoolId: schoolId,
            Month: month,
            Year: year,
            IsSubcontractorPurchaseInvoiceSummaryMonth: true,
          },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "Id",
            order: "ASC",
          },
        })
        .then(({ data }) => {
          const response = data as ISubcontractorRouteTransaction[];
          const lastSubcontractorRouteTransactions: IRoutePricePreview[] =
            response.map((subcontractorRouteTransaction) => ({
              baseAmount: subcontractorRouteTransaction.baseAmount,
              busAssistantIncentive:
                subcontractorRouteTransaction.busAssistantIncentive,
              schoolIncentive: subcontractorRouteTransaction.schoolIncentive,
              additionalIncentive:
                subcontractorRouteTransaction.additionalIncentive,
              adjustmentAmount: subcontractorRouteTransaction.adjustmentAmount,
              vehicleSize: subcontractorRouteTransaction.vehicleSize,
              type: "last",
            }));

          setRoutePrices((prev) => [
            ...prev.filter((item) => item.type !== "last"),
            ...lastSubcontractorRouteTransactions,
          ]);
        })
        .finally(() => setIsLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeName, subcontractorId, schoolId, month, year]);

  // When price input changes, update new price preview using base data from 'last'
  useEffect(() => {
    if (routeName && subcontractorId && schoolId) {
      const updatedNewBreakdowns: IRoutePricePreview[] = routePrices
        .filter((entry) => entry.type === "last")
        .map((entry) => ({
          ...entry,
          type: "new",
          adjustmentAmount: (price || 0) * 100,
        }));

      setRoutePrices((prev) => [
        ...prev.filter((item) => item.type !== "new"),
        ...updatedNewBreakdowns,
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price, routePrices.length]);

  // Check if there are any last month data
  const hasLastData = routePrices.some((x) => x.type === "last");

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} className={classes.routePricingContainer}>
        {isLoading ? (
          <SanitizedGrid
            container
            spacing={2}
            className={classes.loadingIndicator}
          >
            <CircularProgress size={50} />
          </SanitizedGrid>
        ) : routeId && !hasLastData && !isLoading ? (
          <Alert severity="info">
            <Typography variant="body2">
              No last month price preview available for Route {routeName}
            </Typography>
          </Alert>
        ) : (
          <RoutePricePreview routePrices={routePrices} viewType={viewType} />
        )}
      </Grid>
    </Grid>
  );
};

export default RoutePriceComponent;
